import { Icon, Menu, MenuItem, Popover } from '@blueprintjs/core';
import BOSHomeButton from 'Components/Generic/Navigation/BOSHomeButton';
import { useForceUpdater } from 'Data/context/ForceUpdater';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { NavLink } from 'react-router';
import styles from './SideNavMenu.module.css';

const SideNavMenu = ({
  upperMenuItems,
  lowerMenuItems,
  location,
  defaultExpandedMenuItems = [],
}) => {
  const [minified, setMinified] = useState(false);
  const [tempOpen, setTempOpen] = useState(false);
  let isClosed = minified && !tempOpen;
  const [expandedMenus, setExpandedMenus] = useState(defaultExpandedMenuItems);

  // If using location utilize it to auto-expand parent menu of current location
  let [locationMap, setLocationMap] = useState({});

  const { forceParentUpdate, triggerUpdate } = useForceUpdater();

  useLayoutEffect(() => {
    forceParentUpdate();
    triggerUpdate();
  }, [minified]);

  useEffect(() => {
    if (location) {
      let currentLocation = locationMap[location.pathname];
      if (currentLocation?.length > 1) {
        markMenuExpanded(currentLocation[0]);
      }
    }
  }, [location, locationMap]);

  useEffect(() => {
    if (location) {
      let currentLocationMap = { ...locationMap };
      upperMenuItems.forEach((item) => {
        currentLocationMap[item.to] = [item.tabId];
        if (item.menuItems?.length) {
          item.menuItems.forEach((subItem) => {
            currentLocationMap[subItem.to] = [item.tabId, subItem.tabId];
          });
        }
      });
      setLocationMap(currentLocationMap);
    }
  }, [upperMenuItems]);

  const toggleMenuItem = (item) => {
    if (item.menuItems?.length) {
      if (expandedMenus.includes(item.tabId)) {
        setExpandedMenus([...expandedMenus.filter((i) => i !== item.tabId)]);
      } else {
        expandMenu(item);
      }
    }
  };

  const expandMenu = (item) => {
    item.menuItems?.length &&
      !expandedMenus.includes(item.tabId) &&
      setExpandedMenus([
        ...expandedMenus.filter((i) => i !== item.tabId),
        item.tabId,
      ]);
  };

  const markMenuExpanded = (itemTabId) => {
    setExpandedMenus([
      ...expandedMenus.filter((i) => i !== itemTabId),
      itemTabId,
    ]);
  };

  const getTabsForItems = (items) => {
    return (
      items &&
      items.map((item) => {
        let tabItem = getTabLinkContent(item);

        if (item.menuItems?.length) {
          let subMenuHidden = !expandedMenus.includes(item.tabId);
          let height = +item.menuItems.length * 40;
          let submenu = (
            <div
              className={
                subMenuHidden
                  ? styles.sub_tab_group_hidden
                  : styles.sub_tab_group
              }
              style={{
                height: subMenuHidden ? 0 : height,
              }}>
              {item.menuItems.map((subItem) => {
                return getTabSubContent(subItem);
              })}
            </div>
          );

          let popoverMenuItems = item.menuItems.map((subItem) => {
            return (
              <MenuItem
                key={subItem.tabId}
                icon={subItem.icon}
                text={subItem.title}
                href={subItem.href}
                target={subItem.target || '_self'}
                onClick={() => expandMenu(item)}
              />
            );
          });
          let popoverMenu = <Menu>{popoverMenuItems}</Menu>;

          return isClosed ? (
            <Popover
              key={item.tabId}
              disabled={!isClosed}
              content={popoverMenu}
              placement='right'
              interactionKind='hover'
              className={styles.nav_popover}>
              {tabItem}
            </Popover>
          ) : (
            <Fragment key={item.tabId}>
              {tabItem}
              {submenu}
            </Fragment>
          );
        }

        return tabItem;
      })
    );
  };

  const getTabLinkContent = (item) => {
    let linkTitle = item.title && (
      <span className={isClosed ? styles.menu_text_hidden : styles.menu_text}>
        {item.title}
      </span>
    );

    return (
      <NavLink key={item.tabId} to={item.to} end={!isClosed}>
        {({ isActive }) => (
          <div
            className={`${
              !linkTitle || isClosed
                ? styles.tall_tab_centered
                : styles.tall_tab
            } ${
              isActive
                ? isClosed
                  ? styles.active_tab
                  : styles.active_tab_expanded
                : ''
            }`}
            onClick={() => expandMenu(item)}>
            <Icon icon={item.icon} size={16} />
            {linkTitle}

            {item.menuItems?.length > 0 && !isClosed && (
              <Icon
                icon={
                  expandedMenus.includes(item.tabId)
                    ? 'chevron-up'
                    : 'chevron-down'
                }
                style={{ marginLeft: 'auto' }}
                size={16}
                onClick={(e) => {
                  toggleMenuItem(item);
                  e.preventDefault();
                }}
              />
            )}
          </div>
        )}
      </NavLink>
    );
  };

  const getTabSubContent = (subItem) => {
    let subMenuTitle = (
      <span
        className={
          isClosed ? styles.menu_text_hidden : styles.menu_text_sub_menu
        }>
        {subItem.title}
      </span>
    );

    return (
      <NavLink key={subItem.tabId} to={subItem.to}>
        {({ isActive }) => (
          <div
            className={`${styles.sub_tab}  ${
              isActive
                ? isClosed
                  ? styles.active_sub_tab
                  : styles.active_sub_tab_expanded
                : ''
            }`}>
            <Icon icon={subItem.icon} size={14} />
            {subMenuTitle}
          </div>
        )}
      </NavLink>
    );
  };

  let upperMenuContent = getTabsForItems(upperMenuItems);
  let lowerMenuContent = getTabsForItems(lowerMenuItems);

  let openCloseDrawerTab = (
    <div className={styles.drawer_button_box}>
      <div
        className={`${
          isClosed ? styles.drawer_button_centered : styles.drawer_button_right
        }`}
        onClick={() => setMinified(!minified)}>
        <Icon
          icon={minified ? 'double-chevron-right' : 'double-chevron-left'}
        />
      </div>
    </div>
  );

  return (
    <>
      {minified && <div className={styles.nav_box_dummy}></div>}
      <div
        className={
          minified
            ? tempOpen
              ? styles.nav_box_minified_opened
              : styles.nav_box_minified_closed
            : styles.nav_box
        }>
        <div
          className={styles.tab_box}
          onMouseOver={() => setTempOpen(true)}
          onMouseLeave={() => setTempOpen(false)}>
          <div className={styles.home_button_box}>
            <BOSHomeButton
              showText={!isClosed}
              minimal={location?.pathname === '/'}
            />
          </div>
          <div style={{ overflowY: 'auto', overflowX: 'clip' }}>
            {upperMenuContent}
          </div>
          <div className={styles.lower_menu_box}>{lowerMenuContent}</div>
        </div>
        {openCloseDrawerTab}
      </div>
    </>
  );
};

SideNavMenu.propTypes = {
  upperMenuItems: PropTypes.array,
  lowerMenuItems: PropTypes.array,
  defaultExpandedMenuItems: PropTypes.array,
  location: PropTypes.object,
};

export default SideNavMenu;
